import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { memo, useMemo } from 'react';
import { TabMetadata, ValidationState } from '../types/kycFormTabs.types';
import { getValidationIcon } from '../utils/validationIcons';

interface KycTabsListProps {
  tabs: TabMetadata[];
  getEffectiveValidationState: (
    propState: ValidationState,
    formType: string
  ) => ValidationState;
  identificationValidationState: ValidationState;
  formKValidationState: ValidationState;
  formAValidationState: ValidationState;
  canNavigateToTab: (tabId: string) => boolean;
}

const TOTAL_TABS = 5;

/**
 * Renders KYC form tab triggers with validation status indicators.
 * Optimized with memoization for performance.
 */
export const KycTabsList = memo(function KycTabsList({
  tabs,
  getEffectiveValidationState,
  identificationValidationState,
  formKValidationState,
  formAValidationState,
  canNavigateToTab,
}: KycTabsListProps) {
  // Map tab IDs to their validation states for efficient lookup
  const validationStateMap = useMemo(
    () => ({
      identification: identificationValidationState,
      'form-k': formKValidationState,
      'form-a': formAValidationState,
    }),
    [identificationValidationState, formKValidationState, formAValidationState]
  );

  return (
    <TabsList className={`grid w-full grid-cols-${TOTAL_TABS}`}>
      {tabs.map((tab, index) => {
        const IconComponent = tab.icon;
        const isDisabled = !canNavigateToTab(tab.id);

        // Get effective validation state using lookup map
        const propValidationState =
          validationStateMap[tab.id as keyof typeof validationStateMap] ||
          'pending';
        const validationState = getEffectiveValidationState(
          propValidationState,
          tab.id
        );

        return (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            className='text-xs sm:text-sm flex items-center gap-1'
            disabled={isDisabled}
          >
            <IconComponent className='h-3 w-3 sm:h-4 sm:w-4' />
            <span className='hidden sm:inline'>{tab.label}</span>
            <span className='sm:hidden'>{index + 1}</span>
            {getValidationIcon(validationState)}
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
});
